import React, { useEffect, useState, useMemo, useRef } from 'react';
import { CssBaseline } from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import { RootState } from '../../redux/store';
import NavigationMenu from './Navigation/NavigationMenu';
import AppBarComponent from './AppBarComponent';
import DashboardContent from './Dashboard/DashboardContent';
import { useNavigation } from './constants/navigation';
import { getNativeUserFlag } from '../Login/utils/LoginAuthGuard';
import routes from '../../constants/routes';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const rawNavigation = useNavigation();

  // 🔹 Memoize NAVIGATION to prevent new reference each render
  const NAVIGATION = useMemo(() => rawNavigation, [rawNavigation]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [selectedPage, setSelectedPage] = useState<string>('Dashboard');

  useEffect(() => {
    if (!getNativeUserFlag()) {
      navigate(routes.LOGOUT);
    }
  }, [navigate]);

  const getDefaultOpenDropdown = () => {
    const defaultState: Record<string, boolean> = {};
    NAVIGATION.forEach((item) => {
      if (item.children && item.children.length > 0) {
        defaultState[String(item.value)] = false;
      }
    });
    return defaultState;
  };

  const [openDropdown, setOpenDropdown] = useState<Record<string, boolean>>(
    getDefaultOpenDropdown
  );

  const { user } = useSelector((state: RootState) => state.profile);
  const { paymentUser } = useSelector((state: RootState) => state.paymentUser);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const closeAllDropdowns = () => {
    setOpenDropdown(getDefaultOpenDropdown());
  };

  const handleDrawerMouseEnter = () => {
    if (!isPinned) setOpen(true);
  };

  const handleDrawerMouseLeave = () => {
    if (!isPinned) {
      setOpen(false);

      // Reset dropdowns but keep the one with selected child open if pinned
      const updatedDropdowns: Record<string, boolean> = {};
      NAVIGATION.forEach((item) => {
        const hasSelectedChild = item.children?.some(
          (child) => child.value === selectedPage
        );
        updatedDropdowns[String(item.value)] = isPinned && !!hasSelectedChild;
      });

      setOpenDropdown(updatedDropdowns);
    }
  };

  const handleTogglePinned = () => {
    setIsPinned((prev) => {
      const newPinned = !prev;
      setOpen(newPinned);

      if (!newPinned) {
        // Collapse all dropdowns
        const updatedDropdowns: Record<string, boolean> = {};
        NAVIGATION.forEach((item) => {
          updatedDropdowns[String(item.value)] = false;
        });
        setOpenDropdown(updatedDropdowns);
      }

      return newPinned;
    });
  };

  const handleSelectPage = (page: string) => {
    setSelectedPage(page);

    // Open dropdown for the selected page
    const updatedDropdowns: Record<string, boolean> = {};
    NAVIGATION.forEach((item) => {
      const hasSelectedChild = item.children?.some(
        (child) => child.value === page
      );
      updatedDropdowns[String(item.value)] = !!hasSelectedChild;
    });

    setOpenDropdown(updatedDropdowns);
    setIsPinned(true);
    setOpen(true);
  };

  const handleDropdownToggle = (menuTitle: string) => {
    if (!open) return;

    setOpenDropdown((prevDropdownState) => {
      const newState: Record<string, boolean> = {};
      Object.keys(prevDropdownState).forEach((key) => {
        newState[key] = key === menuTitle ? !prevDropdownState[key] : false;
      });
      return newState;
    });
  };

  useEffect(() => {
    console.log('ether user', user?.email);
    console.log('payment user', paymentUser?.email);
  }, [paymentUser?.email, user?.email]);

  // 🔹 Handle browser back navigation
  useEffect(() => {
    const handlePopState = () => {
      setSelectedPage('Dashboard');
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // 🔹 Mixpanel tracking for Dashboard page
  useEffect(() => {
    if (selectedPage === 'Dashboard') {
      mixpanel.track('admin_home', {
        session_type: 'admin_app',
        'Admin app home page viewed': new Date().toISOString(),
        user_email: user?.email
      });
    }
  }, [selectedPage, user?.email]);

  // 🔹 Update dropdown state when selected page changes (robust deps handling)
  const prevSelectedRef = useRef(selectedPage);
  useEffect(() => {
    // Run only when selectedPage actually changed
    if (prevSelectedRef.current === selectedPage) return;
    prevSelectedRef.current = selectedPage;

    if (!open && !isPinned) return; // respect collapsed/unpinned state

    for (const navItem of NAVIGATION) {
      const isChild = navItem.children?.some(
        (child) => child.value === selectedPage
      );
      if (isChild) {
        setOpenDropdown((prev) => {
          const key = String(navItem.value);
          if (prev[key]) return prev; // already open
          return { ...prev, [key]: true };
        });
        break;
      }
    }
  }, [selectedPage, open, isPinned, NAVIGATION]);

  return (
    <>
      <CssBaseline />
      <AppBarComponent
        open={open}
        handleDrawerToggle={() => {}}
        anchorEl={anchorEl}
        handleMenuOpen={handleMenuOpen}
        handleMenuClose={handleMenuClose}
      />
      <NavigationMenu
        open={open}
        openDropdown={openDropdown}
        handleDropdownToggle={handleDropdownToggle}
        selectedPage={selectedPage}
        setSelectedPage={handleSelectPage}
        isPinned={isPinned}
        handleTogglePinned={handleTogglePinned}
        onMouseEnter={handleDrawerMouseEnter}
        onMouseLeave={handleDrawerMouseLeave}
      />
      <DashboardContent
        open={open}
        selectedPage={selectedPage}
        setSelectedPage={setSelectedPage}
        closeDropdown={closeAllDropdowns}
      />
    </>
  );
}
